import { useState, useRef, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    ClickAwayListener,
    Divider,
    Grid,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    OutlinedInput,
    Paper,
    Popper,
    Stack,
    Switch,
    Typography
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import UpgradePlanCard from './UpgradePlanCard';
import User1 from 'assets/images/users/user-round.svg';
import Cookies from 'js-cookie';
import { useQueryClient } from '@tanstack/react-query';

// assets
import { IconLogout, IconSearch, IconSettings, IconUser } from '@tabler/icons';
import { Lock } from '@mui/icons-material';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
    const theme = useTheme();
    const customization = useSelector((state) => state.customization);
    const navigate = useNavigate();
    const [sdm, setSdm] = useState(true);
    const [value, setValue] = useState('');
    const [notification, setNotification] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [open, setOpen] = useState(false);
    const queryClient = useQueryClient();
    /**
     * anchorRef is used on different componets and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef(null);
    const handleLogout = async () => {
        // console.log('Logout');
        Cookies.remove('jwt', { path: '/' });
        Cookies.remove('name', { path: '/' });
        Cookies.remove('email', { path: '/' });
        Cookies.remove('investor-search', { path: '/' });
        Cookies.remove('deal-index', { path: '/' });
        Cookies.remove('investor-index', { path: '/' });
        Cookies.remove('master-index', { path: '/' });
        Cookies.remove('user_id', { path: '/' });
        Cookies.remove('slug', { path: '/' });
        Cookies.remove('profile_image', { path: '/' });
        Cookies.remove('user_role', { path: '/' });
        localStorage.removeItem('permission');
        Cookies.remove('permission_id', { path: '/' });
        Cookies.remove('syndicate_id', { path: '/' });
        // window.location.reload();
        navigate('/login');
        setOpen(false);
        queryClient.clear();
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleListItemClick = (event, index, route = '') => {
        setSelectedIndex(index);
        handleClose(event);

        if (route && route !== '') {
            navigate(route);
        }
    };
    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <div style={{
                borderWidth: '2px',
                borderColor: theme.palette.primary.dark,
                borderStyle: 'solid',
                borderRadius: '12px',
                width: '50px',
                height: '50px'
            }}>
                <Chip
                    sx={{
                        height: '50px',
                        width: '50px',
                        alignItems: 'center',
                        borderRadius: '12px', // ✅ Was '50%' for circle
                        transition: 'all .2s ease-in-out',
                        borderColor: theme.palette.primary.dark,
                        background: `${theme.palette.primary.dark}!important`,
                        '&[aria-controls="menu-list-grow"], &:hover': {
                            borderColor: theme.palette.primary.dark,
                            background: `${theme.palette.primary.dark}!important`,
                            color: theme.palette.primary.light,
                            '& svg': {
                                stroke: theme.palette.primary.light
                            }
                        },
                        '& .MuiChip-label': {
                            lineHeight: 0
                        }
                    }}
                    icon={
                        <Avatar
                            src={Cookies.get('profile_image') || ''}
                            // src={User1}
                            sx={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '6px', // ✅ Optional for squared avatar
                                margin: '8px -17px 8px 7px !important',
                                cursor: 'pointer'
                            }}
                            ref={anchorRef}
                            aria-controls={open ? 'menu-list-grow' : undefined}
                            aria-haspopup="true"
                            color="inherit"
                        />
                    }
                    variant="outlined"
                    ref={anchorRef}
                    aria-controls={open ? 'menu-list-grow' : undefined}
                    aria-haspopup="true"
                    onClick={handleToggle}
                    color="primary"
                />
            </div>
            <Popper
                placement="bottom-end"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal={false}
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [24, 24]
                            }
                        }
                    ]
                }}
                style={{ zIndex: 1300, borderRadius: '12px', boxShadow: "rgba(0, 0, 0, 0.1) 0px 4px 20px" }}
            >
                {({ TransitionProps }) => (
                    <Transitions in={open} {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]} >
                                    <Box sx={{ p: 2 }}>
                                        <Stack>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Typography variant="h4">
                                                    {Cookies.get('name').charAt(0).toUpperCase() + Cookies.get('name').slice(1)}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="subtitle2">{Cookies.get('email')}</Typography>
                                        </Stack>

                                    </Box>
                                    <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}>
                                        <Box sx={{ p: 2, pt: 0, pb: 1 }}>
                                            <Divider />
                                            <List
                                                component="nav"
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 350,
                                                    minWidth: 300,
                                                    backgroundColor: theme.palette.background.paper,
                                                    borderRadius: '10px',
                                                    [theme.breakpoints.down('md')]: {
                                                        minWidth: '100%'
                                                    },
                                                    '& .MuiListItemButton-root': {
                                                        mt: 0.5
                                                    }
                                                }}
                                            >
                                                <ListItemButton
                                                    sx={{
                                                        borderRadius: `${customization.borderRadius}px`,
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.primary.light, // <- Change to your desired hover color
                                                            color: '#fff', // Optional: text color on hover
                                                            '& .MuiListItemIcon-root': {
                                                                color: 'black' // Optional: icon color on hover
                                                            }
                                                        }
                                                    }}
                                                    selected={selectedIndex === 3}
                                                    onClick={() => navigate('/change-password')}
                                                >
                                                    <ListItemIcon>
                                                        <Lock stroke={1.5} sx={{ fontSize: '1.3rem' }} />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography variant="body2">Change Password</Typography>} />
                                                </ListItemButton>
                                                <ListItemButton
                                                    sx={{
                                                        borderRadius: `${customization.borderRadius}px`,
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.primary.light, // <- Change this color
                                                            color: '#fff',
                                                            '& .MuiListItemIcon-root': {
                                                                color: 'black'
                                                            }
                                                        }
                                                    }}
                                                    selected={selectedIndex === 4}
                                                    onClick={handleLogout}
                                                >
                                                    <ListItemIcon>
                                                        <IconLogout stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography variant="body2">Logout</Typography>} />
                                                </ListItemButton>
                                            </List>
                                        </Box>
                                    </PerfectScrollbar>
                                </MainCard>
                            </ClickAwayListener>
                        </Paper>
                    </Transitions>
                )}
            </Popper>
        </>
    );
};

export default ProfileSection;
